import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import searchFill from '@iconify/icons-eva/search-fill';
import roundFilterList from '@iconify/icons-ic/round-filter-list';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import PaymentIcon from '@mui/icons-material/Payment';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GridOnIcon from '@mui/icons-material/GridOn';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { MdOutlineSystemUpdateAlt } from 'react-icons/md';
import { orderBy as lodashOrderBy } from 'lodash';

import {
  Box,
  Button,
  Card,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Select,
  MenuItem,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Menu,
  Stepper,
  Step,
  StepLabel,
  TextField
} from '@mui/material';
import Scrollbar from '../../../components/Scrollbar';
import SearchNotFound from '../../../components/SearchNotFound';
import SimplePagination from '../../../components/SimplePagination';
import { UserListHead } from '../../../components/_dashboard/user';
import Label from '../../../components/Label';
import { RootStyle, SearchStyle, StyledTableRow, StyledTableCell } from '../../Global/Styling';

// ── Table Head ────────────────────────────────────────────────────────────
const TABLE_HEAD = [
  { id: 'period', label: 'Period' },
  { id: 'returnType', label: 'Return' },
  { id: 'taxableValue', label: 'Taxable Value' },
  { id: 'cgst', label: 'CGST' },
  { id: 'sgst', label: 'SGST' },
  { id: 'itcClaimed', label: 'ITC Claimed' },
  { id: 'netLiability', label: 'Net Liability' },
  { id: 'filingStatus', label: 'Filing' },
  { id: 'paymentStatus', label: 'Payment' },
  { id: 'actions', label: 'ACTIONS' }
];

// ── Mock Data (replace with API) ──────────────────────────────────────────
const MOCK_RETURNS = [
  {
    id: 1,
    period: 'Apr 2024',
    month: 'April',
    year: '2024',
    returnType: 'GSTR-3B',
    taxableValue: 300000,
    cgst: 13500,
    sgst: 13500,
    igst: 36000,
    itcClaimed: 28000,
    netLiability: 35000,
    filingStatus: 'Filed',
    paymentStatus: 'Paid',
    gstin: '27AABCT1234H2Z5',
    totalGST: 63000,
    dueDate: '20-05-2024',
    challanNo: 'CHN-2024-04-001'
  },
  {
    id: 2,
    period: 'May 2024',
    month: 'May',
    year: '2024',
    returnType: 'GSTR-3B',
    taxableValue: 420000,
    cgst: 18900,
    sgst: 18900,
    igst: 0,
    itcClaimed: 32000,
    netLiability: 5800,
    filingStatus: 'Filed',
    paymentStatus: 'Paid',
    gstin: '27AABCT1234H2Z5',
    totalGST: 37800,
    dueDate: '20-06-2024',
    challanNo: 'CHN-2024-05-002'
  },
  {
    id: 3,
    period: 'Jun 2024',
    month: 'June',
    year: '2024',
    returnType: 'GSTR-3B',
    taxableValue: 280000,
    cgst: 12600,
    sgst: 12600,
    igst: 54000,
    itcClaimed: 44000,
    netLiability: 35200,
    filingStatus: 'Pending',
    paymentStatus: 'Pending',
    gstin: '27AABCT1234H2Z5',
    totalGST: 79200,
    dueDate: '20-07-2024',
    challanNo: ''
  },
  {
    id: 4,
    period: 'Jul 2024',
    month: 'July',
    year: '2024',
    returnType: 'GSTR-3B',
    taxableValue: 510000,
    cgst: 22950,
    sgst: 22950,
    igst: 0,
    itcClaimed: 38000,
    netLiability: 7900,
    filingStatus: 'Pending',
    paymentStatus: 'Pending',
    gstin: '27AABCT1234H2Z5',
    totalGST: 45900,
    dueDate: '20-08-2024',
    challanNo: ''
  }
];

const MONTHS = [
  { v: '', label: 'All Months' },
  { v: '04', label: 'April' },
  { v: '05', label: 'May' },
  { v: '06', label: 'June' },
  { v: '07', label: 'July' },
  { v: '08', label: 'August' },
  { v: '09', label: 'September' },
  { v: '10', label: 'October' },
  { v: '11', label: 'November' },
  { v: '12', label: 'December' },
  { v: '01', label: 'January' },
  { v: '02', label: 'February' },
  { v: '03', label: 'March' }
];
const FY_OPTIONS = ['2024-25', '2023-24', '2022-23'];
const PAYMENT_MODES = ['Challan', 'NEFT', 'RTGS', 'Online Banking'];
const PAY_STEPS = ['Review Details', 'Payment Mode', 'Confirmation'];

const fmt = (n) => `₹ ${(n || 0).toLocaleString('en-IN')}`;
const filingColor = (s) =>
  ({ Filed: 'success', Pending: 'warning', Overdue: 'error' }[s] || 'warning');
const payColor = (s) => ({ Paid: 'success', Pending: 'warning', Overdue: 'error' }[s] || 'warning');

// ── DetailRow helper ────────────────────────────────────────────────────
function DR({ label, value }) {
  return (
    <Box>
      <Typography
        variant="caption"
        sx={{ color: '#718096', fontSize: 11, fontWeight: 500, display: 'block' }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, color: '#2d3748', fontSize: 13 }}>
        {value || '–'}
      </Typography>
    </Box>
  );
}

export default function GSTReturn() {
  const navigate = useNavigate();

  // ── Table state ──
  const [filterName, setFilterName] = useState('');
  const [selectedFY, setSelectedFY] = useState(' ');
  const [filterMonth, setFilterMonth] = useState('');
  const [isOpenFilter, setOpenFilter] = useState(false);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('period');

  const [exportAnchor, setExportAnchor] = useState(null);

  // download dialog
  const [dlOpen, setDlOpen] = useState(false);
  const [dlYear, setDlYear] = useState('2024-25');
  const [dlMonth, setDlMonth] = useState('');

  // ── GSTR-3B Challan Dialog ──
  const [challanOpen, setChallanOpen] = useState(false);
  const [challanGenerated, setChallanGenerated] = useState(false);
  const [challanMonth, setChallanMonth] = useState('');
  const [challanFY, setChallanFY] = useState('2024-25');
  const [challanData, setChallanData] = useState(null);

  // ── Pay GST Dialog (3-step) ──
  const [payOpen, setPayOpen] = useState(false);
  const [payRecord, setPayRecord] = useState(null);
  const [payStep, setPayStep] = useState(0);
  const [payDone, setPayDone] = useState(false);
  const [payForm, setPayForm] = useState({ mode: '', challanNumber: '', paymentDate: '' });

  // ── Sorting ──
  const handleRequestSort = (_, property) => {
    setOrder(orderBy === property && order === 'asc' ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const filtered = useMemo(() => {
    let data = [...MOCK_RETURNS];
    if (filterName)
      data = data.filter((r) => r.period.toLowerCase().includes(filterName.toLowerCase()));
    return lodashOrderBy(data, [orderBy], [order]);
  }, [filterName, order, orderBy]);

  // ── Challan handlers ──
  const openChallanDialog = () => {
    setChallanOpen(true);
    setChallanGenerated(false);
    setChallanMonth('');
    setChallanData(null);
  };
  const handleGenerateChallan = () => {
    const mn = MONTHS.findIndex((m) => m.label === challanMonth);
    setChallanData({
      challanNo: `GST-${challanFY.split('-')[0]}-${String(mn).padStart(2, '0')}-001`,
      dueDate: `15-${String(mn + 1).padStart(2, '0')}-${challanFY.split('-')[0]}`,
      amount: 125000,
      period: `${challanMonth} ${challanFY}`
    });
    setChallanGenerated(true);
  };
  const resetChallanDialog = () => {
    setChallanOpen(false);
    setChallanGenerated(false);
    setChallanData(null);
  };

  // ── Pay GST handlers ──
  const openPayDialog = (row) => {
    setPayRecord(row);
    setPayStep(0);
    setPayDone(false);
    setPayForm({ mode: '', challanNumber: '', paymentDate: '' });
    setPayOpen(true);
  };
  const resetPayDialog = () => {
    setPayOpen(false);
    setPayRecord(null);
    setPayStep(0);
    setPayDone(false);
  };
  const handlePayNext = () => {
    if (payStep < PAY_STEPS.length - 1) setPayStep((s) => s + 1);
  };
  const handlePayBack = () => {
    if (payStep > 0) setPayStep((s) => s - 1);
  };
  const handlePaySubmit = () => {
    // TODO: API call to record payment
    // console.log('GST Payment submitted:', payRecord?.id, payForm);
    setPayDone(true);
  };

  const handleExport = (type) => {
    setExportAnchor(null);
    console.log(`Export as ${type}`);
  };

  const handleDownload = () => {
    const monthLabel = MONTHS.find((m) => m.v === dlMonth)?.label || 'All Months';
    const rows = dlMonth
      ? MOCK_RETURNS.filter((r) => r.period.slice(0, 2) === dlMonth || true)
      : MOCK_RETURNS;
    const tableRows = rows
      .map(
        (r) => `
                    <tr>
                        <td>${r.period}</td>
                        <td>${r.returnType || 'GSTR-3B'}</td>
                        <td style="color:${r.filingStatus === 'Filed' ? '#16a34a' : '#dc2626'}">${
          r.filingStatus
        }</td>
                        <td>₹ ${(r.cgst || 0).toLocaleString('en-IN')}</td>
                        <td>₹ ${(r.sgst || 0).toLocaleString('en-IN')}</td>
                        <td>₹ ${(r.igst || 0).toLocaleString('en-IN')}</td>
                        <td><strong>₹ ${(r.totalTax || 0).toLocaleString('en-IN')}</strong></td>
                        <td style="color:${r.paymentStatus === 'Paid' ? '#16a34a' : '#dc2626'}">${
          r.paymentStatus
        }</td>
                    </tr>`
      )
      .join('');
    const html = `<!DOCTYPE html><html><head><title>GST Returns – FY ${dlYear} – ${monthLabel}</title>
                <style>
                    body { font-family: Arial, sans-serif; padding: 32px; color: #1e293b; }
                    h2  { color: #0d4689; margin-bottom: 4px; }
                    p   { color: #64748b; font-size: 13px; margin-top: 0; margin-bottom: 20px; }
                    table { width: 100%; border-collapse: collapse; font-size: 13px; }
                    th  { background: #0d4689; color: #fff; padding: 8px 10px; text-align: left; }
                    td  { padding: 7px 10px; border-bottom: 1px solid #e2e8f0; }
                    tr:nth-child(even) td { background: #f8fafc; }
                    @media print { body { padding: 16px; } }
                </style></head><body>
                <h2>GST Returns Report</h2>
                <p>Financial Year: <strong>FY ${dlYear}</strong> &nbsp;|&nbsp; Month: <strong>${monthLabel}</strong> &nbsp;|&nbsp; Generated: <strong>${new Date().toLocaleDateString(
      'en-IN'
    )}</strong></p>
                <table>
                    <thead><tr><th>Period</th><th>Return Type</th><th>Filing Status</th><th>CGST</th><th>SGST</th><th>IGST</th><th>Total Tax</th><th>Payment</th></tr></thead>
                    <tbody>${tableRows}</tbody>
                </table>
                <script>window.onload = () => { window.print(); }<\/script>
                </body></html>`;
    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    setDlOpen(false);
  };

  return (
    <Box>
      <Card>
        {/* ── Collapsible Filter Bar ── */}
        {isOpenFilter && (
          <Box px={3} pt={2} pb={2} sx={{ borderBottom: '1px solid #eaedf1', bgcolor: '#fafbfc' }}>
            <Grid container spacing={2} alignItems="flex-end">
              {/* Month */}
              <Grid item xs={12} sm={4}>
                <Typography className="formLabel" sx={{ mb: 0.5 }}>
                  Month
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    displayEmpty
                    value={filterMonth}
                    onChange={(e) => setFilterMonth(e.target.value)}
                    sx={{ fontSize: 13 }}
                  >
                    {MONTHS.map((m) => (
                      <MenuItem key={m.v} value={m.v} sx={{ fontSize: 13 }}>
                        {m.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {/* Financial Year */}
              <Grid item xs={12} sm={4}>
                <Typography className="formLabel" sx={{ mb: 0.5 }}>
                  Financial Year
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    displayEmpty
                    value={selectedFY}
                    onChange={(e) => setSelectedFY(e.target.value)}
                    sx={{ fontSize: 13 }}
                  >
                    <MenuItem value=" " sx={{ fontSize: 13 }}>
                      All Financial Years
                    </MenuItem>
                    {FY_OPTIONS.map((o) => (
                      <MenuItem key={o} value={o} sx={{ fontSize: 13 }}>
                        FY {o}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {/* Reset */}
              <Grid item xs="auto">
                <Tooltip title="Reset">
                  <IconButton
                    size="small"
                    className="squareIconButton"
                    onClick={() => {
                      setSelectedFY(' ');
                      setFilterMonth('');
                      setFilterName('');
                    }}
                  >
                    <FilterListOffIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* ── Toolbar ── */}
        <RootStyle sx={{ minHeight: 45, borderBottom: '1px solid #eaedf1' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {/* Export icon */}
            {/* <Tooltip title="Export data" placement="top" arrow>
                                    <IconButton className="squareIconButton" onClick={(e) => setExportAnchor(e.currentTarget)}>
                                        <MdOutlineSystemUpdateAlt size={18} />
                                    </IconButton>
                                </Tooltip> */}

            {/* File GSTR-3B / Generate Challan */}
            <Tooltip title="File GSTR-3B / Generate Challan" placement="top" arrow>
              <IconButton className="squareIconButton" onClick={openChallanDialog}>
                <ReceiptLongIcon sx={{ fontSize: '1.1rem' }} />
              </IconButton>
            </Tooltip>

            {/* Download monthly data */}
            <Tooltip title="Download Monthly Data" placement="top" arrow>
              <IconButton className="squareIconButton" onClick={() => setDlOpen(true)}>
                <DownloadIcon sx={{ fontSize: '1.1rem' }} />
              </IconButton>
            </Tooltip>

            {/* Export dropdown menu */}
            <Menu
              anchorEl={exportAnchor}
              open={Boolean(exportAnchor)}
              onClose={() => setExportAnchor(null)}
              PaperProps={{
                sx: { borderRadius: 1, minWidth: 160, boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }
              }}
            >
              <MenuItem onClick={() => handleExport('pdf')} sx={{ fontSize: 13 }}>
                <PictureAsPdfIcon sx={{ fontSize: 16, mr: 1, color: '#dc2626' }} /> Export as PDF
              </MenuItem>
              <MenuItem onClick={() => handleExport('excel')} sx={{ fontSize: 13 }}>
                <GridOnIcon sx={{ fontSize: 16, mr: 1, color: '#16a34a' }} /> Export as Excel
              </MenuItem>
            </Menu>
          </Box>

          <Stack direction="row" alignItems="center" spacing={1}>
            <SearchStyle
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              placeholder="Search period…"
              startAdornment={
                <InputAdornment position="start">
                  <Box component={Icon} icon={searchFill} sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              }
            />
            <Tooltip title="Filters">
              <IconButton className="squareIconButton" onClick={() => setOpenFilter(!isOpenFilter)}>
                <Icon icon={roundFilterList} />
              </IconButton>
            </Tooltip>
          </Stack>
        </RootStyle>

        {/* ── Table ── */}
        <Scrollbar>
          <TableContainer sx={{ minWidth: 900, height: '50vh', overflow: 'auto' }}>
            <Table stickyHeader size="small">
              <UserListHead
                order={order}
                orderBy={orderBy}
                headLabel={TABLE_HEAD}
                rowCount={filtered.length}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {filtered.map((row) => (
                  <StyledTableRow key={row.id}>
                    <StyledTableCell sx={{ fontWeight: 700, color: '#0d4689' }}>
                      {row.period}
                    </StyledTableCell>
                    <StyledTableCell>
                      <Chip
                        label={row.returnType}
                        size="small"
                        sx={{ fontSize: 10, bgcolor: '#e0f2fe', color: '#0369a1', fontWeight: 600 }}
                      />
                    </StyledTableCell>
                    <StyledTableCell>₹ {row.taxableValue.toLocaleString()}</StyledTableCell>
                    <StyledTableCell>₹ {row.cgst.toLocaleString()}</StyledTableCell>
                    <StyledTableCell>₹ {row.sgst.toLocaleString()}</StyledTableCell>
                    <StyledTableCell>₹ {row.itcClaimed.toLocaleString()}</StyledTableCell>
                    <StyledTableCell sx={{ fontWeight: 700 }}>
                      ₹ {row.netLiability.toLocaleString()}
                    </StyledTableCell>
                    <StyledTableCell>
                      <Label variant="ghost" color={filingColor(row.filingStatus)}>
                        {row.filingStatus}
                      </Label>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Label variant="ghost" color={payColor(row.paymentStatus)}>
                        {row.paymentStatus}
                      </Label>
                    </StyledTableCell>

                    {/* ── ACTION column: 3 icons ── */}
                    <StyledTableCell width={110}>
                      <Stack direction="row" spacing={0.5}>
                        {/* 1. Pay GST — only for pending rows → opens inline dialog */}
                        {row.paymentStatus !== 'Paid' ? (
                          <Tooltip title="Pay GST">
                            <IconButton
                              size="small"
                              className="squareIconButton"
                              onClick={() => openPayDialog(row)}
                            >
                              <PaymentIcon sx={{ fontSize: '1rem' }} />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Box sx={{ width: 30 }} />
                        )}

                        {/* 2. View Details */}
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            className="squareIconButton"
                            onClick={() =>
                              navigate(`/accounting/gst/return/view/${row.id}`, {
                                state: { record: row }
                              })
                            }
                          >
                            <VisibilityOutlinedIcon sx={{ fontSize: '1rem' }} />
                          </IconButton>
                        </Tooltip>

                        {/* 3. Download */}
                        <Tooltip title="Download">
                          <IconButton
                            size="small"
                            className="squareIconButton"
                            onClick={() => console.log('TODO: Download', row.period)}
                          >
                            <DownloadIcon sx={{ fontSize: '1rem' }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
              {filtered.length === 0 && (
                <TableBody>
                  <TableRow>
                    <TableCell align="center" colSpan={10} sx={{ py: 4 }}>
                      <SearchNotFound searchQuery={filterName} />
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </Scrollbar>
        <SimplePagination page={page} setPage={setPage} hasNextPage={false} />
      </Card>

      {/* ═══════════════════════════════════════════════════════════
                    PAY GST DIALOG — 3 steps: Review | Payment Mode | Confirm
                    ═══════════════════════════════════════════════════════════ */}
      <Dialog open={payOpen} onClose={resetPayDialog} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#0d4689' }}>
            Pay GST — {payRecord?.period}
          </Typography>
          <IconButton size="small" onClick={resetPayDialog}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />

        <DialogContent>
          {!payDone ? (
            <Box pt={1}>
              {/* ── Stepper ── */}
              <Stepper activeStep={payStep} alternativeLabel sx={{ mb: 3 }}>
                {PAY_STEPS.map((label) => (
                  <Step key={label}>
                    <StepLabel
                      sx={{
                        '& .MuiStepLabel-label': { fontSize: 12 },
                        '& .MuiStepIcon-root.Mui-active': { color: '#0d4689' },
                        '& .MuiStepIcon-root.Mui-completed': { color: '#4fbbc8' }
                      }}
                    >
                      {label}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>

              {/* ── Step 0: Review Details ── */}
              {payStep === 0 && (
                <Box>
                  <Typography variant="body2" sx={{ color: '#64748b', mb: 2, fontSize: 13 }}>
                    Review GST details before proceeding with payment.
                  </Typography>
                  <Box
                    sx={{
                      bgcolor: '#eff6ff',
                      p: 2.5,
                      borderRadius: 1.5,
                      border: '1px solid #bfdbfe'
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <DR label="Period" value={payRecord?.period} />
                      </Grid>
                      <Grid item xs={6}>
                        <DR label="Return Type" value={payRecord?.returnType} />
                      </Grid>
                      <Grid item xs={6}>
                        <DR label="GSTIN" value={payRecord?.gstin} />
                      </Grid>
                      <Grid item xs={6}>
                        <DR label="Due Date" value={payRecord?.dueDate} />
                      </Grid>
                      <Grid item xs={12}>
                        <Divider sx={{ my: 0.5 }} />
                      </Grid>
                      <Grid item xs={4}>
                        <DR label="Total GST" value={fmt(payRecord?.totalGST)} />
                      </Grid>
                      <Grid item xs={4}>
                        <DR label="ITC Claimed" value={fmt(payRecord?.itcClaimed)} />
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ bgcolor: '#0d4689', borderRadius: 1, p: 1.5 }}>
                          <Typography
                            variant="caption"
                            sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 10, display: 'block' }}
                          >
                            Amount to Pay
                          </Typography>
                          <Typography sx={{ color: '#4fbbc8', fontWeight: 800, fontSize: 16 }}>
                            {fmt(payRecord?.netLiability)}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              )}

              {/* ── Step 1: Payment Mode ── */}
              {payStep === 1 && (
                <Box>
                  <Typography variant="body2" sx={{ color: '#64748b', mb: 2.5, fontSize: 13 }}>
                    Choose your payment method and enter the challan details.
                  </Typography>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12}>
                      <Typography className="formLabel" sx={{ mb: 0.5 }}>
                        Payment Mode
                      </Typography>
                      <Select
                        fullWidth
                        size="small"
                        displayEmpty
                        className="textInput"
                        value={payForm.mode}
                        onChange={(e) => setPayForm((p) => ({ ...p, mode: e.target.value }))}
                      >
                        <MenuItem value="">Select Mode</MenuItem>
                        {PAYMENT_MODES.map((m) => (
                          <MenuItem key={m} value={m}>
                            {m}
                          </MenuItem>
                        ))}
                      </Select>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography className="formLabel" sx={{ mb: 0.5 }}>
                        Challan Number
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        className="textInput"
                        placeholder="Enter challan no."
                        value={payForm.challanNumber}
                        onChange={(e) =>
                          setPayForm((p) => ({ ...p, challanNumber: e.target.value }))
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography className="formLabel" sx={{ mb: 0.5 }}>
                        Payment Date
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        type="date"
                        className="textInput"
                        value={payForm.paymentDate}
                        onChange={(e) => setPayForm((p) => ({ ...p, paymentDate: e.target.value }))}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* ── Step 2: Confirmation ── */}
              {payStep === 2 && (
                <Box>
                  <Typography variant="body2" sx={{ color: '#64748b', mb: 2.5, fontSize: 13 }}>
                    Confirm the payment details below and click <strong>Complete Payment</strong>.
                  </Typography>
                  <Box
                    sx={{
                      bgcolor: '#f0fdf4',
                      p: 2.5,
                      borderRadius: 1.5,
                      border: '1px solid #86efac'
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <DR label="Period" value={payRecord?.period} />
                      </Grid>
                      <Grid item xs={6}>
                        <DR label="Amount" value={fmt(payRecord?.netLiability)} />
                      </Grid>
                      <Grid item xs={6}>
                        <DR label="Payment Mode" value={payForm.mode || '—'} />
                      </Grid>
                      <Grid item xs={6}>
                        <DR label="Challan No." value={payForm.challanNumber || '—'} />
                      </Grid>
                      <Grid item xs={6}>
                        <DR label="Payment Date" value={payForm.paymentDate || '—'} />
                      </Grid>
                      <Grid item xs={6}>
                        <DR label="GSTIN" value={payRecord?.gstin} />
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              )}
            </Box>
          ) : (
            /* ── Success State ── */
            <Box py={3} textAlign="center">
              <CheckCircleOutlineIcon sx={{ fontSize: 60, color: '#16a34a', mb: 1.5 }} />
              <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#15803d', mb: 0.5 }}>
                Payment Recorded Successfully!
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontSize: 13 }}>
                GST payment for <strong>{payRecord?.period}</strong> has been recorded. Amount:{' '}
                <strong>{fmt(payRecord?.netLiability)}</strong>
              </Typography>
            </Box>
          )}
        </DialogContent>

        <Divider />
        <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
          {!payDone ? (
            <>
              {/* Cancel / Back */}
              {payStep === 0 ? (
                <Button onClick={resetPayDialog} sx={{ textTransform: 'none', color: '#64748b' }}>
                  Cancel
                </Button>
              ) : (
                <Button onClick={handlePayBack} sx={{ textTransform: 'none', color: '#64748b' }}>
                  Back
                </Button>
              )}

              {/* Next / Complete */}
              {payStep < PAY_STEPS.length - 1 ? (
                <Button
                  variant="contained"
                  className="mainBtn"
                  onClick={handlePayNext}
                  disabled={payStep === 1 && !payForm.mode}
                  sx={{ textTransform: 'none' }}
                >
                  Next
                </Button>
              ) : (
                <Button
                  variant="contained"
                  className="mainBtn"
                  onClick={handlePaySubmit}
                  sx={{ textTransform: 'none' }}
                >
                  Complete Payment
                </Button>
              )}
            </>
          ) : (
            <Button
              variant="contained"
              className="mainBtn"
              onClick={resetPayDialog}
              sx={{ textTransform: 'none' }}
            >
              Done
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ═══════════════════════════════════════════════════════════
                    GSTR-3B CHALLAN DIALOG
                    ═══════════════════════════════════════════════════════════ */}
      <Dialog open={challanOpen} onClose={resetChallanDialog} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#0d4689' }}>
            GST Payment Challan
          </Typography>
          <IconButton size="small" onClick={resetChallanDialog}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {!challanGenerated ? (
            <Box pt={1}>
              <Typography variant="body2" sx={{ color: '#64748b', mb: 2.5, fontSize: 13 }}>
                Select the period for which you want to generate the GST challan.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography className="formLabel">Month</Typography>
                  <Select
                    fullWidth
                    size="small"
                    displayEmpty
                    className="textInput"
                    value={challanMonth}
                    onChange={(e) => setChallanMonth(e.target.value)}
                  >
                    <MenuItem value="">Select Month</MenuItem>
                    {MONTHS.map((m) => (
                      <MenuItem key={m} value={m}>
                        {m}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography className="formLabel">Financial Year</Typography>
                  <Select
                    fullWidth
                    size="small"
                    className="textInput"
                    value={challanFY}
                    onChange={(e) => setChallanFY(e.target.value)}
                  >
                    {FY_OPTIONS.map((fy) => (
                      <MenuItem key={fy} value={fy}>
                        {fy}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
              </Grid>
            </Box>
          ) : (
            <Box pt={1}>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2.5,
                    py: 0.8,
                    bgcolor: '#dcfce7',
                    borderRadius: '999px'
                  }}
                >
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#16a34a' }} />
                  <Typography sx={{ fontSize: 13, color: '#16a34a', fontWeight: 700 }}>
                    Challan Generated Successfully
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ border: '1px solid #eaedf1', borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ bgcolor: '#0d4689', px: 3, py: 1.5 }}>
                  <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>
                    GST Challan for {challanData?.period}
                  </Typography>
                </Box>
                <Box sx={{ p: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography
                        variant="caption"
                        sx={{ color: '#64748b', fontSize: 11, display: 'block' }}
                      >
                        Challan No.
                      </Typography>
                      <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#0d4689', mt: 0.3 }}>
                        {challanData?.challanNo}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        variant="caption"
                        sx={{ color: '#64748b', fontSize: 11, display: 'block' }}
                      >
                        Due Date
                      </Typography>
                      <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#dc2626', mt: 0.3 }}>
                        {challanData?.dueDate}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 0.5 }} />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        variant="caption"
                        sx={{ color: '#64748b', fontSize: 11, display: 'block' }}
                      >
                        Amount
                      </Typography>
                      <Typography sx={{ fontWeight: 800, fontSize: 22, color: '#0d4689', mt: 0.3 }}>
                        ₹ {challanData?.amount?.toLocaleString('en-IN')}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
          {!challanGenerated ? (
            <>
              <Button onClick={resetChallanDialog} sx={{ textTransform: 'none', color: '#64748b' }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                className="mainBtn"
                disabled={!challanMonth}
                onClick={handleGenerateChallan}
                sx={{ textTransform: 'none' }}
              >
                Generate Challan
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={resetChallanDialog}
                startIcon={<CloseIcon />}
                sx={{ textTransform: 'none', color: '#64748b' }}
              >
                Close
              </Button>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={() => window.print()}
                sx={{ textTransform: 'none' }}
              >
                Print
              </Button>
              <Button
                variant="contained"
                className="mainBtn"
                startIcon={<DownloadIcon />}
                onClick={() => console.log('TODO: Download Challan PDF', challanData)}
                sx={{ textTransform: 'none' }}
              >
                Download PDF
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* DOWNLOAD MONTHLY DATA DIALOG */}
      <Dialog open={dlOpen} onClose={() => setDlOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#0d4689' }}>
            Download GST Returns
          </Typography>
          <IconButton size="small" onClick={() => setDlOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Box pt={1}>
            <Typography variant="body2" sx={{ color: '#64748b', mb: 2.5, fontSize: 13 }}>
              Select Financial Year and Month to generate and download a PDF report.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography className="formLabel" sx={{ mb: 0.5 }}>
                  Financial Year
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={dlYear}
                    onChange={(e) => setDlYear(e.target.value)}
                    sx={{ fontSize: 13 }}
                  >
                    {FY_OPTIONS.map((fy) => (
                      <MenuItem key={fy} value={fy} sx={{ fontSize: 13 }}>
                        FY {fy}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Typography className="formLabel" sx={{ mb: 0.5 }}>
                  Month
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    displayEmpty
                    value={dlMonth}
                    onChange={(e) => setDlMonth(e.target.value)}
                    sx={{ fontSize: 13 }}
                  >
                    {MONTHS.map((m) => (
                      <MenuItem key={m.v} value={m.v} sx={{ fontSize: 13 }}>
                        {m.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
          <Button onClick={() => setDlOpen(false)} sx={{ textTransform: 'none', color: '#64748b' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            className="mainBtn"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            sx={{ textTransform: 'none' }}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
